import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, Images, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";

// Live camera barcode/QR scanner. `continuous` keeps the camera open for multiple scans.
export function ScannerDialog({ open, onClose, onResult, title = "Scan", hint, continuous = false, wide = true }) {
  const regionId = useRef(`scan-${Math.random().toString(36).slice(2)}`).current;
  const fileId = `${regionId}-file`;
  const scanner = useRef(null);
  const last = useRef({ text: "", at: 0 });
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const fileRef = useRef(null);

  const emit = (text) => {
    const now = Date.now();
    if (text === last.current.text && now - last.current.at < 2500) return;
    last.current = { text, at: now };
    const keep = onResult(text.trim());
    setCount((c) => c + 1);
    if (navigator.vibrate) navigator.vibrate(80);
    if (!continuous || keep === false) onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setError(""); setCount(0);
    const t = setTimeout(async () => {
      try {
        const s = new Html5Qrcode(regionId, { verbose: false });
        scanner.current = s;
        await s.start({ facingMode: "environment" },
          { fps: 12, qrbox: wide ? { width: 280, height: 140 } : { width: 240, height: 240 } },
          (text) => alive && emit(text), () => {});
      } catch (e) {
        if (alive) setError("Camera unavailable. Allow camera access or scan from a photo.");
      }
    }, 150);
    return () => {
      alive = false; clearTimeout(t);
      const s = scanner.current; scanner.current = null;
      if (s) s.stop().then(() => s.clear()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const scanPhoto = async (file) => {
    if (!file) return;
    try {
      const fs = new Html5Qrcode(fileId, { verbose: false });
      const text = await fs.scanFile(file, false);
      fs.clear();
      emit(text);
    } catch { toast.error("No barcode or QR found. Try a clearer photo or type it in."); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden bg-slate-950 text-white border-slate-800" data-testid="scanner-dialog">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-white"><Camera size={18} className="text-brand-400" /> {title}</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">{hint || "Keep the barcode or QR inside the frame"}</DialogDescription>
        </DialogHeader>
        <div className="relative mx-5 rounded-xl overflow-hidden bg-black aspect-[4/3]">
          <div id={regionId} className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          {error && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-brand-300" data-testid="scanner-error">{error}</div>}
          {continuous && count > 0 && <span className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-mono font-bold rounded-full px-2.5 py-1" data-testid="scanner-count">{count} scanned</span>}
        </div>
        <div id={fileId} className="hidden" />
        <div className="flex gap-2 p-5">
          <Button type="button" variant="outline" className="flex-1 h-11 rounded-full bg-transparent border-slate-700 text-white hover:bg-slate-800 hover:text-white"
            data-testid="scanner-photo-btn" onClick={() => fileRef.current?.click()}>
            <Images size={16} className="mr-1.5" /> Scan photo
          </Button>
          <Button type="button" className="flex-1 h-11 rounded-full bg-brand-500 hover:bg-brand-400 text-slate-950 font-bold" data-testid="scanner-close-btn" onClick={onClose}>
            <X size={16} className="mr-1.5" /> {continuous ? "Done" : "Close"}
          </Button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="scanner-photo-input"
            onChange={(e) => { scanPhoto(e.target.files[0]); e.target.value = ""; }} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
